import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLobbyFlow } from "./hooks/useLobbyFlow";
import { LobbyEntryPanel } from "./components/LobbyEntryPanel";
import { LobbySessionPanel } from "./components/LobbySessionPanel";
import { saveNickname } from "../../shared/session";

const NETWORK_ERROR_PATTERN = /failed to fetch|load failed|networkerror|network request failed/i;
const MAINTENANCE_MESSAGE = "The site is currently under maintenance. Please try again later.";

export function LobbyPage() {
  const navigate = useNavigate();
  const {
    session,
    snapshot,
    rooms,
    loading,
    error,
    showReconnecting,
    countdownSecondsLeft,
    formations,
    handleCreateRoom,
    handleJoinRoom,
    handleLeave,
    handleStartAuction,
    chooseFormation,
    createGameMode,
    setCreateGameMode,
    createMinPoolRating,
    setCreateMinPoolRating,
    createPoolLeague,
    setCreatePoolLeague,
    poolMinRatingOptions,
    poolMinRatingOptionsLoaded,
  } = useLobbyFlow();

  const maxPlayers = snapshot?.maxPlayers ?? 2;
  const gameMode = snapshot?.gameMode ?? "DUEL";

  const [nicknameInput, setNicknameInput] = useState(session.nickname);
  const isInSession = snapshot?.joined === true;
  const nicknameReady = nicknameInput.trim().length >= 2;
  const hasSelectablePoolRating = poolMinRatingOptions.some((o) => o.selectable);
  const canCreateRoom = nicknameReady
    && (!poolMinRatingOptionsLoaded || hasSelectablePoolRating);

  useEffect(() => {
    if (!error) return;
    if (NETWORK_ERROR_PATTERN.test(error)) {
      toast.error(MAINTENANCE_MESSAGE, { id: "lobby-maintenance" });
      return;
    }
    toast.error(error);
  }, [error]);

  useEffect(() => {
    if (showReconnecting) {
      toast.loading("Reconnecting live updates...", { id: "lobby-reconnect" });
      return;
    }
    toast.dismiss("lobby-reconnect");
  }, [showReconnecting]);

  const onCreateRoom = () => {
    const trimmed = nicknameInput.trim().substring(0, 20);
    saveNickname(trimmed);
    handleCreateRoom();
  };

  const onJoinRoom = (roomSessionId: string) => {
    const trimmed = nicknameInput.trim().substring(0, 20);
    saveNickname(trimmed);
    handleJoinRoom(roomSessionId);
  };

  return (
    <div className="lobby-bg box-border flex h-dvh min-h-0 w-full flex-col overflow-hidden p-3 sm:p-4">
      {!isInSession && (
        <div className="flex min-h-0 flex-1 flex-col">
        <LobbyEntryPanel
          nicknameInput={nicknameInput}
          onNicknameChange={setNicknameInput}
          nicknameReady={nicknameReady}
          createGameMode={createGameMode}
          onCreateGameModeChange={setCreateGameMode}
          createPoolLeague={createPoolLeague}
          onCreatePoolLeagueChange={setCreatePoolLeague}
          createMinPoolRating={createMinPoolRating}
          onCreateMinPoolRatingChange={setCreateMinPoolRating}
          poolMinRatingOptions={poolMinRatingOptions}
          poolMinRatingOptionsLoaded={poolMinRatingOptionsLoaded}
          hasSelectablePoolRating={hasSelectablePoolRating}
          canCreateRoom={canCreateRoom}
          loading={loading}
          rooms={rooms}
          onCreateRoom={onCreateRoom}
          onJoinRoom={onJoinRoom}
          onSpectateRoom={(sessionId) => navigate(`/spectate/${sessionId}`)}
        />
        </div>
      )}

      {isInSession && snapshot && (
        <div className="flex min-h-0 flex-1 flex-col">
        <LobbySessionPanel
          nickname={session.nickname}
          snapshot={snapshot}
          maxPlayers={maxPlayers}
          gameMode={gameMode}
          countdownSecondsLeft={countdownSecondsLeft}
          formations={formations}
          loading={loading}
          onChooseFormation={chooseFormation}
          onStartAuction={handleStartAuction}
          onLeave={handleLeave}
        />
        </div>
      )}
    </div>
  );
}
